import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import ChevronLeft from 'lucide-react/dist/esm/icons/chevron-left.js';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right.js';
import MessageCircle from 'lucide-react/dist/esm/icons/message-circle.js';
import X from 'lucide-react/dist/esm/icons/x.js';
import { useLanguage } from '@/contexts/LanguageContext';
import { handleWhatsAppClick } from '../lib/whatsappTelemetry.js';

const WORKS_PAGE_IMAGE_NAMES = [
  'hero_10.webp',
  'hero_11.webp',
  'hero_12.webp',
  'hero_13.webp',
  'hero_14.webp',
  'hero_15.webp',
  'hero_16.webp',
  'hero_17.webp',
  'hero_18.webp',
  'hero_19.webp',
  'hero_2.webp',
  'hero_22.webp',
  'hero_23.webp',
  'hero_24.webp',
  'hero_25.webp',
  'hero_26.webp',
  'hero_27.webp',
  'hero_28.webp',
  'hero_29.webp',
  'hero_3.webp',
  'hero_30.webp',
  'hero_31.webp',
  'hero_32.webp',
  'hero_33.webp',
  'hero_34.webp',
  'hero_35.webp',
  'hero_36.webp',
  'hero_37.webp',
  'hero_4.webp',
  'hero_6.webp',
  'hero_7.webp',
  'hero_8.webp',
  'hero_9.webp',
];
const FALLBACK_IMAGES = WORKS_PAGE_IMAGE_NAMES.map((name) => `/hero_images/works_page/${name}`);
const INITIAL_MOBILE_COUNT = 8;
const INITIAL_DESKTOP_COUNT = 16;

const FEATURED_IMAGE_NAMES = [
  'hero_10.webp',
  'hero_17.webp',
  'hero_18.webp',
  'hero_23.webp',
  'hero_26.webp',
  'hero_27.webp',
  'hero_34.webp',
  'hero_35.webp',
  'hero_36.webp',
  'hero_30.webp',
  'hero_22.webp',
  'hero_8.webp',
  'hero_16.webp',
  'hero_31.webp',
  'hero_33.webp',
  'hero_15.webp',
];

const IMAGE_ALT_TEXT = {
  'hero_10.webp': {
    he: 'חולצה לבנה עם איור צבעוני של טיגריס ועצי דקל',
    en: 'White T-shirt with a colorful tiger and palm-tree graphic',
  },
  'hero_17.webp': {
    he: 'קפוצ׳ון שחור עם איור גולגולת גדול וטיפוגרפיה לבנה',
    en: 'Black hoodie with a large white skull and typography graphic',
  },
  'hero_18.webp': {
    he: 'איור סמוראי צבעוני על בגד שחור',
    en: 'Colorful samurai illustration on a black garment',
  },
  'hero_23.webp': {
    he: 'הדפס צבעוני של גולגולת וטיפוגרפיה על בגד שחור עם רוכסן',
    en: 'Colorful skull and lettering print on a black zip garment',
  },
  'hero_26.webp': {
    he: 'בגדים שחורים עם מספר סמלי מבשלה לבנים',
    en: 'Black garments with several white brewery logos',
  },
  'hero_27.webp': {
    he: 'בגד שחור עם גרפיקה ירוקה ולבנה של Team Fiverr',
    en: 'Black garment with a green-and-white Team Fiverr graphic',
  },
  'hero_34.webp': {
    he: 'בגדים בצבע בורדו ואפור עם סמלים מאוירים וכיתוב בעברית',
    en: 'Burgundy and grey garments with illustrated emblems and Hebrew text',
  },
  'hero_35.webp': {
    he: 'סמל כנפיים רקום על בד זית',
    en: 'Embroidered wing insignia on olive fabric',
  },
  'hero_36.webp': {
    he: 'טלאי עגול רקום על בד זית',
    en: 'Circular embroidered patch on olive fabric',
  },
  'hero_30.webp': {
    he: 'חולצה שחורה עם לוגו WiLCO צבעוני',
    en: 'Black T-shirt with a colorful WiLCO logo',
  },
  'hero_22.webp': {
    he: 'קפוצ׳ון שחור עם רוכסן ואיור סמוראי',
    en: 'Black zip hoodie with a samurai illustration',
  },
  'hero_8.webp': {
    he: 'חולצה לבנה עם איור מסכה באדום ושחור',
    en: 'White T-shirt with a red-and-black mask illustration',
  },
  'hero_16.webp': {
    he: 'קפוצ׳ון אפור עם איור גולגולת באדום ושחור',
    en: 'Grey hoodie with a red-and-black skull graphic',
  },
  'hero_31.webp': {
    he: 'ז׳קט כהה עם סמל לבן גדול',
    en: 'Dark jacket with a large white emblem',
  },
  'hero_33.webp': {
    he: 'חולצת פולו שחורה עם לוגו Electra בזהב',
    en: 'Black polo shirt with a gold Electra logo',
  },
  'hero_15.webp': {
    he: 'קפוצ׳ון אפור עם לוגו Huuge באדום ולבן',
    en: 'Grey hoodie with a red-and-white Huuge logo',
  },
  'hero_11.webp': {
    he: 'בגד שחור עם הדפס צבעוני של רחוב עירוני ודמות בחזית',
    en: 'Black garment with a colorful city-street print and a figure in the foreground',
  },
  'hero_12.webp': {
    he: 'בגד שחור עם סמל ירוק ואדום וכיתוב בעברית',
    en: 'Black garment with a green-and-red emblem and Hebrew lettering',
  },
  'hero_13.webp': {
    he: 'חולצה שחורה עם איור סמוראי קטן בחזית',
    en: 'Black shirt with a small samurai illustration on the front',
  },
  'hero_14.webp': {
    he: 'חולצה כחולה כהה עם פסים צבעוניים וסמל גאומטרי לבן',
    en: 'Dark blue shirt with colorful stripes and a white geometric emblem',
  },
  'hero_19.webp': {
    he: 'חולצה לבנה לילדים עם כיתוב ורוד וסגול ואיור כתר',
    en: 'White children’s T-shirt with pink and purple lettering and a crown graphic',
  },
  'hero_2.webp': {
    he: 'בגד שחור עם איור פנטזיה צבעוני וסרט ורוד',
    en: 'Black garment with a colorful fantasy illustration and a pink ribbon',
  },
  'hero_24.webp': {
    he: 'חולצה כחולה כהה עם עיטור פרחים צבעוני וכיתוב בעברית',
    en: 'Dark blue shirt with a colorful floral ornament and Hebrew lettering',
  },
  'hero_25.webp': {
    he: 'חולצה שחורה עם גרפיקה כחולה ואפורה בצורת סימן שאלה',
    en: 'Black shirt with a blue-and-grey question-mark-shaped graphic',
  },
  'hero_28.webp': {
    he: 'חולצה כחולה כהה עם תרשים צבעוני וכיתוב באנגלית',
    en: 'Dark blue shirt with a colorful diagram and English lettering',
  },
  'hero_29.webp': {
    he: 'חולצה כהה עם כיתוב לבן ואיור קטן של רוכב אופניים',
    en: 'Dark shirt with white lettering and a small cyclist illustration',
  },
  'hero_3.webp': {
    he: 'חולצה שחורה עם סמל גאומטרי צבעוני וכיתוב לבן',
    en: 'Black T-shirt with a colorful geometric emblem and white lettering',
  },
  'hero_32.webp': {
    he: 'חולצה שחורה עם גרפיקה מופשטת של קווים לבנים',
    en: 'Black shirt with an abstract white line graphic',
  },
  'hero_37.webp': {
    he: 'בד אפור עם סמל מלבני בצבעים כחול, ירוק ולבן',
    en: 'Grey fabric with a rectangular blue, green and white emblem',
  },
  'hero_4.webp': {
    he: 'חולצה צהובה עם כיתוב לבן גדול ופרטי צבע קטנים',
    en: 'Yellow shirt with large white lettering and small colored details',
  },
  'hero_6.webp': {
    he: 'חולצה שחורה עם איור ירוק וסגול במרכז',
    en: 'Black T-shirt with a green-and-purple illustration in the center',
  },
  'hero_7.webp': {
    he: 'בגד שחור עם איור צבעוני של קטנוע',
    en: 'Black garment with a colorful scooter illustration',
  },
  'hero_9.webp': {
    he: 'ערימת בגדים שחורים מקופלים עם כיתוב לבן',
    en: 'Stack of folded black garments with white lettering',
  },
};

const WHATSAPP_PHONE = '972503969640';

const getImageName = (path) => path.split('/').pop()?.split('?')[0] || path;

const curateImages = (paths) => {
  const uniquePaths = [...new Set(paths)];
  const pathsByName = new Map(uniquePaths.map((path) => [getImageName(path), path]));
  const featuredNames = new Set(FEATURED_IMAGE_NAMES);
  const featuredPaths = FEATURED_IMAGE_NAMES.map((name) => pathsByName.get(name)).filter(Boolean);
  const remainingPaths = uniquePaths.filter((path) => !featuredNames.has(getImageName(path)));

  return [...featuredPaths, ...remainingPaths];
};

const useMobileGallery = () => {
  const [isMobile, setIsMobile] = useState(() => (
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 639px)').matches : false
  ));

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 639px)');
    const handleChange = (event) => setIsMobile(event.matches);

    setIsMobile(mediaQuery.matches);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  return isMobile;
};

const Works = () => {
  const { language, isRTL } = useLanguage();
  const shouldReduceMotion = useReducedMotion();
  const isMobile = useMobileGallery();
  const [heroImages, setHeroImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [usingFallback, setUsingFallback] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const closeButtonRef = useRef(null);
  const firstRevealedImageRef = useRef(null);
  const lightboxOpenerRef = useRef(null);
  const shouldFocusRevealedImageRef = useRef(false);
  const shouldRestoreLightboxFocusRef = useRef(false);

  const copy = language === 'he'
    ? {
        title: 'העבודות שלנו – Printeam',
        description: 'עבודות אמיתיות מהסטודיו של Printeam — הצצה לעיצובים ולבגדים ממותגים שהפקנו.',
        eyebrow: 'עבודות אמיתיות מהסטודיו',
        heading: 'העבודות שלנו',
        intro: 'הצצה להזמנות אמיתיות שייצרנו — מהעיצוב ועד הבגד המוגמר.',
        startOrder: 'התחילו הזמנה',
        methods: 'לשיטות המיתוג',
        loading: 'טוענים עבודות מהסטודיו…',
        fallback: 'לא הצלחנו לטעון את הגלריה המלאה כרגע, לכן מוצג מבחר חלופי מעבודות הסטודיו.',
        openImage: (index, total) => `פתיחת עבודה ${index + 1} מתוך ${total}`,
        genericAlt: (index, total) => `עבודת מיתוג אמיתית מהסטודיו של Printeam, תמונה ${index + 1} מתוך ${total}`,
        showMore: 'עוד עבודות',
        remaining: (count) => `${count} נוספות`,
        close: 'סגירת התמונה',
        previous: 'התמונה הקודמת',
        next: 'התמונה הבאה',
        imagePosition: (index, total) => `תמונה ${index + 1} מתוך ${total}`,
        dialogTitle: 'תצוגה מוגדלת של עבודה מהסטודיו',
        finalHeading: 'ראיתם כיוון שמתאים לכם?',
        finalBody: 'בחרו בגד מהקטלוג או שלחו לנו את העיצוב שלכם, ונעזור להפוך אותו להזמנה מוכנה.',
        whatsapp: 'דברו איתנו בוואטסאפ',
        whatsappMessage: 'שלום, ראיתי עבודה שאהבתי בגלריה של printeam.co.il ואשמח להתייעץ לגבי הזמנה.',
      }
    : {
        title: 'Our Work – Printeam',
        description: 'Real work from the Printeam studio — a look at designs and branded garments we have produced.',
        eyebrow: 'REAL WORK FROM THE STUDIO',
        heading: 'Our Work',
        intro: 'A look at real orders we have produced, from the original design to the finished garment.',
        startOrder: 'Start an order',
        methods: 'Branding methods',
        loading: 'Loading work from the studio…',
        fallback: 'The full gallery is not available right now, so a fallback selection of studio work is shown.',
        openImage: (index, total) => `Open work ${index + 1} of ${total}`,
        genericAlt: (index, total) => `Real production work from the Printeam studio, image ${index + 1} of ${total}`,
        showMore: 'More work',
        remaining: (count) => `${count} more`,
        close: 'Close image',
        previous: 'Previous image',
        next: 'Next image',
        imagePosition: (index, total) => `Image ${index + 1} of ${total}`,
        dialogTitle: 'Enlarged view of studio work',
        finalHeading: 'Seen a direction that fits?',
        finalBody: 'Choose a garment from the catalog or send us your design, and we will help turn it into a finished order.',
        whatsapp: 'Talk to us on WhatsApp',
        whatsappMessage: 'Hi, I saw work I liked in the gallery on printeam.co.il and would like to ask about an order.',
      };

  useEffect(() => {
    let isMounted = true;

    const fetchManifest = async () => {
      try {
        const res = await fetch('/hero_images/works_page/manifest.json', { cache: 'force-cache' });
        if (!res.ok) throw new Error(`Unexpected status ${res.status}`);
        const list = await res.json();
        if (!isMounted) return;
        setHeroImages(curateImages(Array.isArray(list) ? list.filter(Boolean) : []));
        setUsingFallback(false);
      } catch (e) {
        if (!isMounted) return;
        console.warn('[Works] Falling back to default gallery list:', e?.message || e);
        setHeroImages(curateImages(FALLBACK_IMAGES));
        setUsingFallback(true);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchManifest();
    return () => {
      isMounted = false;
    };
  }, []);

  const initialCount = isMobile ? INITIAL_MOBILE_COUNT : INITIAL_DESKTOP_COUNT;
  const visibleImages = useMemo(
    () => (isExpanded ? heroImages : heroImages.slice(0, initialCount)),
    [heroImages, initialCount, isExpanded],
  );
  const remainingCount = Math.max(0, heroImages.length - visibleImages.length);
  const isLightboxOpen = lightboxIndex !== null;
  const activeImage = lightboxIndex === null ? null : heroImages[lightboxIndex];

  const getAltText = useCallback((path, index) => {
    const authoredAlt = IMAGE_ALT_TEXT[getImageName(path)]?.[language];
    return authoredAlt || copy.genericAlt(index, heroImages.length);
  }, [copy, heroImages.length, language]);

  const openLightbox = useCallback((index, opener) => {
    lightboxOpenerRef.current = opener;
    setLightboxIndex(index);
  }, []);

  const closeLightbox = useCallback(() => {
    shouldRestoreLightboxFocusRef.current = true;
    setLightboxIndex(null);
  }, []);

  const showPreviousImage = useCallback(() => {
    setLightboxIndex((currentIndex) => {
      if (currentIndex === null || heroImages.length === 0) return currentIndex;
      return (currentIndex - 1 + heroImages.length) % heroImages.length;
    });
  }, [heroImages.length]);

  const showNextImage = useCallback(() => {
    setLightboxIndex((currentIndex) => {
      if (currentIndex === null || heroImages.length === 0) return currentIndex;
      return (currentIndex + 1) % heroImages.length;
    });
  }, [heroImages.length]);

  useEffect(() => {
    if (!isLightboxOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.requestAnimationFrame(() => closeButtonRef.current?.focus());

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isLightboxOpen]);

  useEffect(() => {
    if (!isLightboxOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeLightbox();
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        if (isRTL) showNextImage();
        else showPreviousImage();
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        if (isRTL) showPreviousImage();
        else showNextImage();
        return;
      }

      if (event.key !== 'Tab') return;

      const dialog = document.querySelector('[data-works-lightbox]');
      const focusableElements = dialog
        ? [...dialog.querySelectorAll('button:not([disabled]), a[href]')]
        : [];

      if (focusableElements.length === 0) return;
      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      } else if (!event.shiftKey && document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeLightbox, isLightboxOpen, isRTL, showNextImage, showPreviousImage]);

  useEffect(() => {
    if (!isExpanded || !shouldFocusRevealedImageRef.current) return;
    shouldFocusRevealedImageRef.current = false;
    window.requestAnimationFrame(() => firstRevealedImageRef.current?.focus());
  }, [isExpanded]);

  const handleShowMore = () => {
    shouldFocusRevealedImageRef.current = true;
    setIsExpanded(true);
  };

  const handleLightboxExitComplete = () => {
    if (!shouldRestoreLightboxFocusRef.current) return;
    shouldRestoreLightboxFocusRef.current = false;
    lightboxOpenerRef.current?.focus();
  };

  const whatsappLink = `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(copy.whatsappMessage)}`;

  return (
    <>
      <div className="works-page min-h-screen bg-white text-[#111116]" dir={isRTL ? 'rtl' : 'ltr'}>
        <section
          className="relative isolate overflow-hidden border-b border-black/[0.06] bg-[#fbfafc]"
          aria-labelledby="works-heading"
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-full opacity-70"
            style={{
              background: 'radial-gradient(circle at 50% 5%, rgba(177, 0, 255, 0.11), transparent 44%)',
            }}
          />
          <motion.div
            className="mx-auto max-w-5xl px-5 py-16 text-center sm:px-8 sm:py-20 lg:py-24"
            initial={shouldReduceMotion ? false : { opacity: 0, y: 20 }}
            animate={shouldReduceMotion ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="mb-4 text-sm font-medium tracking-[0.08em] text-[#7600ff] sm:text-base">
              {copy.eyebrow}
            </p>
            <h1
              id="works-heading"
              className="text-4xl font-normal leading-[1.05] tracking-[-0.025em] text-[#111116] sm:text-6xl lg:text-7xl"
            >
              {copy.heading}
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg font-normal leading-8 text-[#62616a] sm:text-xl">
              {copy.intro}
            </p>
            <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
              <Link
                to="/catalog"
                className="inline-flex min-h-12 items-center justify-center rounded-full px-7 py-3 text-base font-medium text-white shadow-[0_12px_32px_rgba(122,0,255,0.22)] transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7600ff] focus-visible:ring-offset-4 motion-reduce:transform-none motion-reduce:transition-none"
                style={{ background: 'linear-gradient(90deg, #7600ff 0%, #f000d8 100%)' }}
              >
                {copy.startOrder}
              </Link>
              <Link
                to="/methods-of-branding"
                className="inline-flex min-h-12 items-center justify-center rounded-full px-6 py-3 text-base font-medium text-[#2f2e37] underline decoration-[#b8b5c0] decoration-1 underline-offset-8 transition-colors hover:text-[#7600ff] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7600ff] focus-visible:ring-offset-4"
              >
                {copy.methods}
              </Link>
            </div>
          </motion.div>
        </section>

        <section className="px-5 py-12 sm:px-8 sm:py-16 lg:px-10 lg:py-20" aria-label={copy.heading}>
          <div className="mx-auto max-w-[1280px]">
            {usingFallback ? (
              <p
                className="mb-8 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm leading-6 text-amber-900"
                role="status"
              >
                {copy.fallback}
              </p>
            ) : null}

            {isLoading ? (
              <div className="flex min-h-64 items-center justify-center text-center text-[#6f6d76]" role="status">
                {copy.loading}
              </div>
            ) : (
              <>
                <div
                  className="works-masonry columns-1 gap-6 sm:columns-2 md:columns-3 lg:columns-4"
                  style={{ columnGap: 'clamp(1.25rem, 2vw, 2rem)' }}
                >
                  {visibleImages.map((path, index) => (
                    <motion.button
                      key={path}
                      ref={isExpanded && index === initialCount ? firstRevealedImageRef : null}
                      type="button"
                      className="group mb-6 block w-full cursor-zoom-in overflow-hidden rounded-[1.25rem] bg-[#f2f1f4] text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7600ff] focus-visible:ring-offset-4 sm:mb-7"
                      style={{ breakInside: 'avoid', WebkitColumnBreakInside: 'avoid' }}
                      initial={shouldReduceMotion ? false : { opacity: 0, y: 18 }}
                      whileInView={shouldReduceMotion ? undefined : { opacity: 1, y: 0 }}
                      whileHover={shouldReduceMotion ? undefined : { y: -3 }}
                      viewport={{ once: true, amount: 0.12 }}
                      transition={{
                        duration: 0.45,
                        delay: shouldReduceMotion ? 0 : Math.min(index, 7) * 0.035,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                      aria-label={copy.openImage(index, heroImages.length)}
                      aria-haspopup="dialog"
                      onClick={(event) => openLightbox(index, event.currentTarget)}
                    >
                      <motion.img
                        src={path}
                        alt={getAltText(path, index)}
                        className="h-auto w-full"
                        loading={index < 4 ? 'eager' : 'lazy'}
                        decoding="async"
                        whileHover={shouldReduceMotion ? undefined : { scale: 1.018 }}
                        transition={{ duration: 0.35, ease: 'easeOut' }}
                      />
                    </motion.button>
                  ))}
                </div>

                {remainingCount > 0 ? (
                  <div className="mt-8 flex justify-center sm:mt-10">
                    <button
                      type="button"
                      onClick={handleShowMore}
                      className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-[#cac7cf] bg-white px-7 py-3 text-base font-medium text-[#222129] transition-colors hover:border-[#7600ff] hover:text-[#7600ff] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7600ff] focus-visible:ring-offset-4"
                    >
                      <span>{copy.showMore}</span>
                      <span className="text-sm text-[#77747e]">{copy.remaining(remainingCount)}</span>
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </section>

        <section className="px-5 pb-14 sm:px-8 sm:pb-20 lg:px-10" aria-labelledby="works-cta-heading">
          <motion.div
            className="mx-auto max-w-[1280px] overflow-hidden rounded-[2rem] px-6 py-12 text-center text-white sm:px-12 sm:py-16 lg:px-20"
            style={{ background: 'linear-gradient(112deg, #6200e8 0%, #a000ff 46%, #ed00c8 100%)' }}
            initial={shouldReduceMotion ? false : { opacity: 0, y: 22 }}
            whileInView={shouldReduceMotion ? undefined : { opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <h2 id="works-cta-heading" className="text-3xl font-normal leading-tight sm:text-5xl">
              {copy.finalHeading}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base font-normal leading-7 text-white/[0.85] sm:text-lg">
              {copy.finalBody}
            </p>
            <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
              <Link
                to="/catalog"
                className="inline-flex min-h-12 items-center justify-center rounded-full bg-white px-7 py-3 text-base font-medium text-[#6500df] transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-4 focus-visible:ring-offset-[#7800ed] motion-reduce:transform-none motion-reduce:transition-none"
              >
                {copy.startOrder}
              </Link>
              <a
                href={whatsappLink}
                onClick={(event) => handleWhatsAppClick(event, 'works')}
                onAuxClick={(event) => handleWhatsAppClick(event, 'works')}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-white/[0.45] bg-white/10 px-7 py-3 text-base font-medium text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-4 focus-visible:ring-offset-[#7800ed]"
              >
                <MessageCircle className="h-5 w-5" aria-hidden="true" />
                {copy.whatsapp}
              </a>
            </div>
          </motion.div>
        </section>
      </div>

      <AnimatePresence onExitComplete={handleLightboxExitComplete}>
        {activeImage ? (
          <motion.div
            key="works-lightbox"
            data-works-lightbox
            role="dialog"
            aria-modal="true"
            aria-labelledby="works-lightbox-title"
            className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/[0.94] px-3 py-16 text-white sm:px-16 sm:py-12"
            dir={isRTL ? 'rtl' : 'ltr'}
            initial={shouldReduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={shouldReduceMotion ? undefined : { opacity: 0 }}
            transition={{ duration: 0.18 }}
            onMouseDown={(event) => {
              if (event.currentTarget === event.target) closeLightbox();
            }}
          >
            <h2 id="works-lightbox-title" className="sr-only">{copy.dialogTitle}</h2>

            <button
              ref={closeButtonRef}
              type="button"
              onClick={closeLightbox}
              className="absolute end-4 top-4 inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/25 bg-black/30 text-white transition-colors hover:bg-white/[0.15] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:end-7 sm:top-7"
              aria-label={copy.close}
            >
              <X className="h-6 w-6" aria-hidden="true" />
            </button>

            <button
              type="button"
              onClick={showPreviousImage}
              className="absolute end-3 top-1/2 z-10 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 bg-black/[0.35] text-white transition-colors hover:bg-white/[0.15] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:end-7 sm:h-14 sm:w-14"
              aria-label={copy.previous}
            >
              {isRTL
                ? <ChevronRight className="h-7 w-7" aria-hidden="true" />
                : <ChevronLeft className="h-7 w-7" aria-hidden="true" />}
            </button>

            <motion.figure
              key={activeImage}
              className="m-0 flex max-h-full max-w-[min(1100px,calc(100vw-5rem))] flex-col items-center justify-center sm:max-w-[min(1100px,calc(100vw-11rem))]"
              initial={shouldReduceMotion ? false : { opacity: 0, scale: 0.985 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
            >
              <img
                src={activeImage}
                alt={getAltText(activeImage, lightboxIndex)}
                className="max-h-[calc(100svh-9rem)] max-w-full rounded-xl object-contain sm:max-h-[calc(100svh-7rem)]"
                decoding="async"
              />
              <figcaption className="mt-3 text-sm tabular-nums text-white/75" aria-live="polite">
                {copy.imagePosition(lightboxIndex, heroImages.length)}
              </figcaption>
            </motion.figure>

            <button
              type="button"
              onClick={showNextImage}
              className="absolute start-3 top-1/2 z-10 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 bg-black/[0.35] text-white transition-colors hover:bg-white/[0.15] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:start-7 sm:h-14 sm:w-14"
              aria-label={copy.next}
            >
              {isRTL
                ? <ChevronLeft className="h-7 w-7" aria-hidden="true" />
                : <ChevronRight className="h-7 w-7" aria-hidden="true" />}
            </button>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
};

export default Works;
